import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AYANAMSA_OPTIONS,
  VARGA_MAPPERS,
  computeGochar,
  computeTransits,
  currentPositions,
  getDefaultAdapter,
} from '@astrally/astro-engine';
import { GRAHA_NAMES, type Planet } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section } from '../../components/common';
import { Term } from '../../components/Term';
import { NorthIndianChart } from './NorthIndianChart';
import { SouthIndianChart } from './SouthIndianChart';
import { EastIndianChart } from './EastIndianChart';
import { WesternWheel } from './WesternWheel';
import { localdb } from '../../lib/localdb';
import { DashaTimeline } from './DashaTimeline';
import { AspectTable, TransitPanel, WesternPlanetTable } from './tables';
import { UncertaintyView } from './UncertaintyView';
import { ChartStyleGuide } from './ChartStyleGuide';
import { GrahaDetail } from './GrahaDetail';

type SystemTab = 'jyotish' | 'western' | 'combined';
/**
 * Regional chart formats. None is "the" default — North India draws a
 * diamond, the South a 4×4 grid, the east its own square style.
 */
type JyotishStyle = 'north' | 'south' | 'east';

/**
 * The label of the ayanamsa the chart was actually computed with. Read from
 * `chart.meta`, not from the app-state setting — during a recalculation the
 * two disagree, and a Lahiri label beside a Raman value is worse than none.
 */
const ayanamsaLabel = (id: string) => AYANAMSA_OPTIONS.find((o) => o.id === id)?.label ?? id;

/** Indian graha name when the body is one of the nine; the id otherwise. */
const INDIAN_NAME: Partial<Record<Planet, string>> = GRAHA_NAMES;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Kundli-first display copy for the varga picker. The engine's own D1
 * description still says "birth chart" (and rides along inside each chart
 * card's footer via VargaChart.description) — overridden here rather than
 * edited there, because the engine file belongs to another change.
 */
const VARGA_NOTES: Record<'D1' | 'D9' | 'D10', string> = {
  D1: 'Rashi — the birth kundli itself, every graha in the rashi it truly occupies',
  D9: VARGA_MAPPERS.D9.description,
  D10: VARGA_MAPPERS.D10.description,
};

export function ChartsScreen() {
  const { t } = useI18n();
  const { chart, chartError, ready, ayanamsaId, setAyanamsa, recalcChart } = useApp();
  const [tab, setTab] = useState<SystemTab>('jyotish');
  const [varga, setVarga] = useState<'D1' | 'D9' | 'D10'>('D1');
  const [showGochar, setShowGochar] = useState(false);
  const [style, setStyle] = useState<JyotishStyle>('north');
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  // The chart format is a lasting preference, not a per-visit toggle.
  useEffect(() => {
    localdb.get<JyotishStyle>('kv', 'jyotishStyle').then((saved) => {
      if (saved === 'north' || saved === 'south' || saved === 'east') setStyle(saved);
    });
  }, []);

  const chooseStyle = (next: JyotishStyle) => {
    setStyle(next);
    void localdb.set('kv', 'jyotishStyle', next);
  };

  const transitData = useMemo(() => {
    if (!showGochar || !chart) return null;
    const moonSign =
      chart.positions?.find((p) => p.planet === 'moon')?.sidereal.sign ??
      chart.uncertainty?.midpointPositions.find((p) => p.planet === 'moon')?.sidereal.sign;
    const ctx = currentPositions(new Date(), getDefaultAdapter());
    const gochar = computeGochar(ctx, moonSign);
    const natalPoints = (chart.positions ?? []).map((p) => ({ id: p.planet, lon: p.lonTropical }));
    const transits = natalPoints.length ? computeTransits(natalPoints, ctx) : [];
    return { gochar, transits };
  }, [showGochar, chart]);

  /** Recomputing from the stored birth input is the only way out of a cache
   *  that will not deserialise. */
  const retryChart = async () => {
    setRetrying(true);
    setRetryFailed(false);
    try {
      // Resolves null when there is no stored birth input left to recompute
      // from; say so rather than leaving the button apparently dead.
      if (!(await recalcChart())) setRetryFailed(true);
    } catch {
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  if (!chart) {
    /* `chart === null` used to render Loading… unconditionally, and nothing
       was ever going to set it: either the cached chart would not deserialise
       (`chartError`), or the session finished loading without one at all
       (`ready`). Only a still-loading session is genuinely still loading. */
    return (
      <div className="screen">
        <ScreenHeader title={t('kundli.title')} />
        {chartError || ready ? (
          <div className="card col">
            <div className="banner banner--danger small">{t('chart.failed')}</div>
            <button
              className="btn btn--primary btn--block"
              disabled={retrying}
              onClick={retryChart}
            >
              {retrying ? <span className="spinner" /> : t('chart.recalculate')}
            </button>
            {retryFailed && (
              <div className="tiny muted">
                Astrally has no stored birth details to recalculate from — re-enter them from{' '}
                <Link to="/profile">your profile</Link>.
              </div>
            )}
          </div>
        ) : (
          <div className="muted">{t('common.loading')}</div>
        )}
      </div>
    );
  }

  const mode = chart.meta.timeConfidence;
  const modeBanner = {
    exact: { title: t('chart.mode.exact.title'), body: t('chart.mode.exact.body'), cls: 'banner--info' },
    approximate: { title: t('chart.mode.approx.title'), body: t('chart.mode.approx.body'), cls: 'banner--gold' },
    unknown: { title: t('chart.mode.unknown.title'), body: t('chart.mode.unknown.body'), cls: 'banner--gold' },
  }[mode];

  const isExact = mode === 'exact';

  return (
    <div className="screen">
      <ScreenHeader
        title={t('kundli.title')}
        subtitle={`${chart.birth.place.label ?? ''} · ${chart.birth.date.year}-${String(chart.birth.date.month).padStart(2, '0')}-${String(chart.birth.date.day).padStart(2, '0')}`}
      />

      <div className={`banner ${modeBanner.cls}`} style={{ marginBottom: 12 }}>
        <b>{modeBanner.title}</b>
        <div style={{ marginTop: 4 }}>{modeBanner.body}</div>
        {mode !== 'exact' && (
          <Link to="/profile/refine">
            <div style={{ marginTop: 8, fontWeight: 600 }}>→ {t('chart.refine.cta')}</div>
          </Link>
        )}
      </div>

      <div className="seg" style={{ marginBottom: 14 }}>
        {(['jyotish', 'western', 'combined'] as SystemTab[]).map((s) => (
          <button
            key={s}
            className={`seg__opt ${tab === s ? 'seg__opt--on' : ''}`}
            onClick={() => setTab(s)}
          >
            {t(`chart.tab.${s}` as 'chart.tab.jyotish')}
          </button>
        ))}
      </div>

      {!isExact && tab !== 'combined' && <UncertaintyView chart={chart} />}

      {isExact && tab === 'jyotish' && (
        <div className="col">
          {/* India draws charts three ways; none of them is "the" default. */}
          <div className="seg">
            {([
              ['north', 'North'],
              ['south', 'South'],
              ['east', 'East'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                className={`seg__opt ${style === value ? 'seg__opt--on' : ''}`}
                onClick={() => chooseStyle(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* One tap from every kundli card: the schematic "how to read this"
              reference for whichever style is on screen. */}
          <ChartStyleGuide style={style} />

          {/* Sidereal school. Changing it recalculates every Jyotish result, so
              all three notes are on screen BEFORE the tap. They used to hang
              off `title`, which no touch device ever shows. */}
          <div className="card" style={{ padding: 10 }}>
            <div className="tiny faint" style={{ marginBottom: 6 }}>
              <Term id="ayanamsa">Ayanamsa</Term> — currently{' '}
              {chart.meta.ayanamsa.valueDeg.toFixed(3)}°
            </div>
            <div className="seg" role="group" aria-label="Ayanamsa">
              {AYANAMSA_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  className={`seg__opt ${ayanamsaId === opt.id ? 'seg__opt--on' : ''}`}
                  aria-pressed={ayanamsaId === opt.id}
                  onClick={() => setAyanamsa(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="col" style={{ gap: 4, marginTop: 8 }}>
              {AYANAMSA_OPTIONS.map((opt) => (
                <div
                  key={opt.id}
                  className={`tiny ${ayanamsaId === opt.id ? 'muted' : 'faint'}`}
                  style={{ lineHeight: 1.5 }}
                >
                  <b>{opt.label}</b> — {opt.note}
                </div>
              ))}
            </div>
          </div>
          {/* Same reasoning as the ayanamsa notes: a divisional chart is
              meaningless as a bare "D9" tab. */}
          <Section title={t('kundli.vargaCharts')} term="varga">
            <div className="col">
              <div className="card" style={{ padding: 10 }}>
                <div className="seg" role="group" aria-label="Divisional chart">
                  {(['D1', 'D9', 'D10'] as const).map((v) => (
                    <button
                      key={v}
                      className={`seg__opt ${varga === v ? 'seg__opt--on' : ''}`}
                      aria-pressed={varga === v}
                      onClick={() => setVarga(v)}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <div className="col" style={{ gap: 4, marginTop: 8 }}>
                  {(['D1', 'D9', 'D10'] as const).map((v) => (
                    <div
                      key={v}
                      className={`tiny ${varga === v ? 'muted' : 'faint'}`}
                      style={{ lineHeight: 1.5 }}
                    >
                      <b>{v}</b> {VARGA_NOTES[v]}
                    </div>
                  ))}
                </div>
              </div>
              {chart.jyotish?.vargas
                .filter((vc) => vc.varga === varga)
                .map((vc) =>
                  style === 'north' ? (
                    <NorthIndianChart key={vc.varga} varga={vc} />
                  ) : style === 'south' ? (
                    <SouthIndianChart key={vc.varga} varga={vc} />
                  ) : (
                    <EastIndianChart key={vc.varga} varga={vc} />
                  ),
                )}
            </div>
          </Section>
          {/* The granular per-graha breakdown follows the varga on screen, so
              the list always explains the drawing directly above it. */}
          <Section title={t('kundli.grahaPositions')} term="graha">
            <GrahaDetail chart={chart} varga={varga} />
          </Section>
          {chart.jyotish?.lagna && (
            <div className="card small muted">
              <Term id="lagna">Lagna</Term>: <b className="gold">{chart.jyotish.lagna.signName}</b>
              {chart.jyotish.lagnaNakshatra && <> · {chart.jyotish.lagnaNakshatra.name}</>}
              {' · '}Ayanamsa ({ayanamsaLabel(chart.meta.ayanamsa.id)}):{' '}
              {chart.meta.ayanamsa.valueDeg.toFixed(4)}°
            </div>
          )}
          {chart.jyotish?.vimshottari && (
            <DashaTimeline
              vim={chart.jyotish.vimshottari}
              note="a 120-year cycle that hands each stretch of life to one graha"
            />
          )}
          {chart.jyotish && chart.jyotish.yogas.filter((y) => y.present).length > 0 && (
            <Section title="Yogas & doshas present" term="yoga">
              <div className="col">
                {chart.jyotish.yogas
                  .filter((y) => y.present)
                  .map((y) => (
                    <div key={y.id} className="card">
                      <div className="spread">
                        <span className="h3">{y.name}</span>
                        <span className={`tiny ${y.kind === 'dosha' ? 'danger' : 'gold'}`}>{y.kind}</span>
                      </div>
                      {/* The engine already writes a plain-language rule for
                          every yoga; "Gaja Kesari" alone tells a newcomer
                          nothing, and the factors below are only evidence. */}
                      <div className="small" style={{ marginTop: 6, lineHeight: 1.5 }}>
                        {y.summary}
                      </div>
                      <div className="tiny faint" style={{ marginTop: 6 }}>{y.factors.join(' · ')}</div>
                    </div>
                  ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {isExact && tab === 'western' && (
        <div className="col">
          <WesternWheel chart={chart} />
          <WesternPlanetTable chart={chart} />
          {chart.western && (
            <Section title={t('chart.aspects')} term="aspect">
              <AspectTable aspects={chart.western.aspects} />
            </Section>
          )}
          {chart.western && chart.western.patterns.length > 0 && (
            <Section title="Chart patterns" term="natal-chart">
              <div className="col">
                {chart.western.patterns.map((p, i) => (
                  <div key={i} className="card small">
                    <b className="accent">{p.type}</b> — {p.detail}{' '}
                    <span className="muted">({p.points.join(', ')})</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {tab === 'combined' && <CombinedView />}

      <div className="divider" />
      <button className="btn btn--ghost btn--block" onClick={() => setShowGochar(!showGochar)}>
        {showGochar ? 'Hide' : 'Show'} {t('kundli.gochar')}
      </button>
      {transitData && (
        <div style={{ marginTop: 12 }}>
          <TransitPanel gochar={transitData.gochar} transits={transitData.transits} />
        </div>
      )}

      {/* Engine version and ephemeris adapter are build telemetry — they told
          the reader nothing. These two are conventions that change the chart. */}
      <div className="tiny faint center" style={{ marginTop: 16 }}>
        House system: {chart.meta.houseSystem} · Nodes: {chart.meta.nodeConvention}
      </div>
    </div>
  );
}

/** Side-by-side comparison — deliberately NOT a merged wheel. */
function CombinedView() {
  const { chart } = useApp();
  if (!chart) return null;
  const positions = chart.positions ?? chart.uncertainty?.midpointPositions ?? [];
  const midpointNote = !chart.positions;

  return (
    <div className="col">
      <div className="card small muted" style={{ lineHeight: 1.55 }}>
        Same sky, two reference frames. <b>Jyotish</b> uses the sidereal zodiac (star-anchored;
        {' '}{ayanamsaLabel(chart.meta.ayanamsa.id)} ayanamsa currently{' '}
        {chart.meta.ayanamsa.valueDeg.toFixed(2)}°) and reads timing
        through dashas and nakshatras. <b>Western</b> uses the tropical zodiac (season-anchored)
        and reads psychology through aspects, houses and transits. Neither is "wrong" — they answer
        differently.
      </div>
      {midpointNote && (
        <div className="banner banner--gold small">
          Positions below use your window midpoint for planets whose sign is stable; see the
          Jyotish/Western tabs for the full stability breakdown.
        </div>
      )}
      <div className="card" style={{ overflowX: 'auto', padding: 10 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Body</th>
              <th>Jyotish (sidereal)</th>
              <th>Western (tropical)</th>
            </tr>
          </thead>
          <tbody>
            {positions
              .filter((p) => !['uranus', 'neptune', 'pluto'].includes(p.planet))
              .map((p) => (
                <tr key={p.planet}>
                  <td>
                    {/* Indian name first — the compare table serves both
                        columns, so the English name rides along in faint. */}
                    {INDIAN_NAME[p.planet] ? (
                      <>
                        {INDIAN_NAME[p.planet]} <span className="faint">({cap(p.planet)})</span>
                      </>
                    ) : (
                      cap(p.planet)
                    )}
                  </td>
                  <td>
                    {p.sidereal.signName} <span className="faint">({p.nakshatra.name})</span>
                  </td>
                  <td>{p.tropical.signName}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
